import { DefaultError, useMutation } from "@tanstack/react-query";
import { queryClient } from "shared/api/queryClient";
import { bulkUpdateModulesByBrandPage } from "../api/bulkUpdateModulesByBrandPage";
import { ModulesListResult } from "../api/getModulesListByBrandPage";
import {
  invalidateModuleListByBrandPageQuery,
  getQueryKey,
} from "../queries/useModuleListByBrandPageQuery";
import { PageModuleData } from "../types";

type Params = {
  brandId: string;
  pageId: string;
  modules: PageModuleData[];
};

export const useReorderPageModulesMutation = () => {
  return useMutation<void, DefaultError, Params>({
    mutationFn: async (variables) => {
      const dataToUpdate = variables.modules.reduce((acc, module) => {
        return {
          ...acc,
          [module.id]: { order: module.order },
        };
      }, {});

      const currentData = queryClient.getQueryData<{
        pages: ModulesListResult[];
        pageParams: any[];
      }>(getQueryKey(variables.brandId, variables.pageId));

      if (currentData) {
        const modulesById = new Map(
          variables.modules.map((module) => [module.id, module]),
        );

        const updatedPages = currentData.pages.map((page) => {
          const updatedModules = page.modules.map((module) => {
            const updatedModule = modulesById.get(module.id);
            if (updatedModule) {
              return { ...module, order: updatedModule.order };
            }
            return module;
          });

          updatedModules.sort((a, b) => a.order - b.order);

          return {
            ...page,
            modules: updatedModules,
          };
        });

        queryClient.setQueryData(
          getQueryKey(variables.brandId, variables.pageId),
          {
            ...currentData,
            pages: updatedPages,
          },
        );
      }

      await bulkUpdateModulesByBrandPage(
        variables.brandId,
        variables.pageId,
        dataToUpdate,
      );
    },
    onSuccess: (_data, variables) => {
      invalidateModuleListByBrandPageQuery(variables.brandId, variables.pageId);
    },
    onError: (_error, variables) => {
      invalidateModuleListByBrandPageQuery(variables.brandId, variables.pageId);
    },
  });
};
